import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

const DoodleCanvas = forwardRef(function DoodleCanvas(
  {
    width,
    height,
    enabled,
    stroke = '#f5b400',
    lineWidth = 2,
    onInkChange,
    onHistoryChange,
  },
  ref,
) {
  const canvasRef = useRef(null);
  const strokesRef = useRef([]);
  const redoRef = useRef([]);
  const activeStrokeRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [hasInk, setHasInk] = useState(false);

  const emitHistoryState = useCallback(() => {
    const canUndo = strokesRef.current.length > 0;
    const canRedo = redoRef.current.length > 0;
    onHistoryChange?.({ canUndo, canRedo, hasInk: canUndo });
  }, [onHistoryChange]);

  const setupCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const w = Math.max(1, Math.floor(width || 1));
    const h = Math.max(1, Math.floor(height || 1));
    const ratio = window.devicePixelRatio || 1;

    canvas.width = Math.floor(w * ratio);
    canvas.height = Math.floor(h * ratio);
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(ratio, ratio);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = stroke;
    ctx.lineWidth = lineWidth;
  }, [height, lineWidth, stroke, width]);

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const w = Math.max(1, Number(width || 1));
    const h = Math.max(1, Number(height || 1));

    ctx.clearRect(0, 0, w, h);
    ctx.strokeStyle = stroke;
    ctx.lineWidth = lineWidth;

    for (const strokeRecord of strokesRef.current) {
      const points = strokeRecord?.points || [];
      if (!points.length) continue;

      ctx.beginPath();
      ctx.moveTo(points[0].x, points[0].y);
      if (points.length === 1) {
        ctx.lineTo(points[0].x + 0.01, points[0].y + 0.01);
      } else {
        for (let index = 1; index < points.length; index += 1) {
          ctx.lineTo(points[index].x, points[index].y);
        }
      }
      ctx.stroke();
      ctx.closePath();
    }
  }, [height, lineWidth, stroke, width]);

  const syncInkState = useCallback(() => {
    const nextHasInk = strokesRef.current.length > 0;
    setHasInk(nextHasInk);
    onInkChange?.(nextHasInk);
    emitHistoryState();
  }, [emitHistoryState, onInkChange]);

  useEffect(() => {
    setupCanvas();
    redraw();
  }, [setupCanvas, redraw]);

  useEffect(() => {
    onInkChange?.(hasInk);
  }, [hasInk, onInkChange]);

  const getPoint = (event) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    const rect = canvas.getBoundingClientRect();
    return {
      x: clamp(event.clientX - rect.left, 0, rect.width),
      y: clamp(event.clientY - rect.top, 0, rect.height),
    };
  };

  const pushPoint = (point) => {
    const strokeRef = activeStrokeRef.current;
    if (!strokeRef || !point) return;
    strokeRef.points.push({ x: point.x, y: point.y });
  };

  const start = (event) => {
    if (!enabled) return;

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const point = getPoint(event);
    if (!point) return;

    event.preventDefault();
    setDrawing(true);
    if (event.currentTarget.setPointerCapture) {
      event.currentTarget.setPointerCapture(event.pointerId);
    }

    const strokeRecord = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      points: [],
    };

    redoRef.current = [];
    strokesRef.current.push(strokeRecord);
    activeStrokeRef.current = strokeRecord;

    pushPoint(point);
    ctx.beginPath();
    ctx.moveTo(point.x, point.y);
  };

  const move = (event) => {
    if (!enabled || !drawing) return;

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const point = getPoint(event);
    if (!point) return;

    event.preventDefault();
    pushPoint(point);
    ctx.lineTo(point.x, point.y);
    ctx.stroke();

    if (!hasInk) setHasInk(true);
  };

  const stop = (event) => {
    if (!drawing) return;
    event.preventDefault();

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    const strokeRecord = activeStrokeRef.current;

    if (ctx) {
      if (strokeRecord?.points?.length === 1) {
        const point = strokeRecord.points[0];
        ctx.lineTo(point.x + 0.01, point.y + 0.01);
        ctx.stroke();
      }
      ctx.closePath();
    }

    activeStrokeRef.current = null;
    setDrawing(false);
    syncInkState();
  };

  useImperativeHandle(ref, () => ({
    clear: () => {
      strokesRef.current = [];
      redoRef.current = [];
      activeStrokeRef.current = null;
      setHasInk(false);
      redraw();
      emitHistoryState();
    },
    undo: () => {
      if (!strokesRef.current.length) return false;
      const removed = strokesRef.current.pop();
      if (removed) {
        redoRef.current.push(removed);
      }
      redraw();
      syncInkState();
      return true;
    },
    redo: () => {
      if (!redoRef.current.length) return false;
      const restored = redoRef.current.pop();
      if (restored) {
        strokesRef.current.push(restored);
      }
      redraw();
      syncInkState();
      return true;
    },
    canUndo: () => strokesRef.current.length > 0,
    canRedo: () => redoRef.current.length > 0,
    hasInk: () => hasInk,
    getCanvasElement: () => canvasRef.current,
    getUserPivots: () => {
      const w = Math.max(1, Number(width || 1));
      const h = Math.max(1, Number(height || 1));

      return strokesRef.current.flatMap((strokeEntry, strokeIdx) =>
        strokeEntry.points.map((point, pointIdx) => ({
          stroke_id: strokeEntry.id,
          stroke_index: strokeIdx,
          point_index: pointIdx,
          x_px: Number(point.x.toFixed(2)),
          y_px: Number(point.y.toFixed(2)),
          x_norm: Number((point.x / w).toFixed(6)),
          y_norm: Number((point.y / h).toFixed(6)),
        })),
      );
    },
  }), [emitHistoryState, hasInk, redraw, syncInkState, width, height]);

  if (!width || !height) return null;

  return (
    <canvas
      ref={canvasRef}
      className={`absolute left-0 top-0 ${enabled ? 'pointer-events-auto cursor-crosshair' : 'pointer-events-none'}`}
      onPointerDown={start}
      onPointerMove={move}
      onPointerUp={stop}
      onPointerCancel={stop}
      onPointerLeave={stop}
      data-testid="doodle-canvas"
    />
  );
});

export default DoodleCanvas;
